import {
    Router,
    RouterStateSnapshot,
    ActivatedRouteSnapshot,
} from '@angular/router';
import { Injectable } from '@angular/core';

import { Observable } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { map, take } from 'rxjs/operators';

import { Hub } from '@qwyk/models';
import { CustomerUsersSelectors, CustomerUsersFeature, CustomerUsersService } from '@qwyk/hub/administration/data-access/customer-users';

import { CustomerUserExistsGuard } from './customer-user-exists.guard';

/**
 * Verifies that the requested portal user exists. Approved (active) portal
 * users of Magaya-integrated (e.g. Magaya Supply Chain) organizations may now
 * be opened using the same portal-user form as non-integrated organizations.
 *
 * This guard always runs after `CustomerUserExistsGuard` on the route (see
 * `canActivate: [CustomerUserExistsGuard, MagayaUserGuard]`), which already
 * loads the user into the store (or redirects on error) before this guard
 * runs. It therefore relies solely on store state instead of re-fetching the
 * user from the API.
 */
@Injectable({
    providedIn: 'root',
})
export class MagayaUserGuard extends CustomerUserExistsGuard {
    constructor(
        store: Store<CustomerUsersFeature.CustomerUsersPartialState>,
        service: CustomerUsersService,
        router: Router
    ) {
        super(store, service, router);
    }

    public canActivate(
        next: ActivatedRouteSnapshot,
        _state: RouterStateSnapshot
    ): Observable<boolean> {
        const id = this.getEntityKey(next);
        return this.getUserFromStore(id).pipe(
            map(user => this.canAccessUser(user, next))
        );
    }

    public canActivateChild(
        next: ActivatedRouteSnapshot,
        state: RouterStateSnapshot
    ): Observable<boolean> {
        return this.canActivate(next, state);
    }

    private getUserFromStore(id: string): Observable<Hub.CustomerUser | null> {
        return this.store.pipe(
            select(CustomerUsersSelectors.getCustomerUsersEntities),
            map(entities => entities[id] || null),
            take(1)
        );
    }

    private canAccessUser(
        user: Hub.CustomerUser | null,
        next: ActivatedRouteSnapshot
    ): boolean {
        if (!user) {
            return false;
        }

        if (user.deactivated) {
            this.redirect(next, 404, 'Not Found');
            return false;
        }

        return true;
    }

    private redirect(next: ActivatedRouteSnapshot, errorCode, errorName): void {
        this.router.navigate([`/error/${errorCode}`], {
            queryParams: {
                source: next.pathFromRoot.map(p => p.url).join('/'),
                errorName: errorName,
            },
        });
    }
}
